import { resetIsContextMenuOpenForLinks, setIsContextMenuOpen } from '../actions/internalActions';
import getComposeLinkViewState from '../selectors/getComposeLinkViewState';
import ComposeLinkViewState from '../store/schema/ComposeLinkViewState';
import { getChangePermissionContextMenuItem } from '../utils/contextMenu/getChangePermissionContextMenuItem';
import { getChangePermissionSharingDialogContextMenuItem } from '../utils/contextMenu/getChangePermissionSharingDialogContextMenuItem';
import { getDividerForContextMenu } from '../utils/contextMenu/getDividerForContextMenu';
import { getEditLinkContextMenuItem } from '../utils/contextMenu/getEditLinkContextMenuItem';
import { getLinkPreviewContextMenuItem } from '../utils/contextMenu/getLinkPreviewContextMenuItem';
import { getRevertToFullUrlContextMenuItem } from '../utils/contextMenu/getRevertToFullUrlContextMenuItem';
import { getSharingTipContextMenuItem } from '../utils/contextMenu/getSharingTipContextMenuItem';
import { observer } from 'mobx-react';
import { assertNever } from 'owa-assert';
import { getOpenInNewTabContextMenuItem } from 'owa-attachment-link-common';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getSharingLink, getSharingTipRecipientInfo } from 'owa-link-data';
import RecipientWellWithFindControlViewState from 'owa-recipient-common/lib/store/schema/RecipientWellWithFindControlViewState';
import { SharingTipRecipientInfo } from 'owa-sharing-data';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { Editor } from 'roosterjs-editor-core';
import { PositionType } from 'roosterjs-editor-types';
import getLinkContextMenuOptions, {
    LinkContextMenuOptions,
} from '../utils/contextMenu/LinkContextMenuOptions';
import {
    ContextualMenu,
    DirectionalHint,
    IContextualMenuItem,
} from 'office-ui-fabric-react/lib/ContextualMenu';

const styles = require('./LinkContextMenu.scss');
const classNames = require('classnames/bind').bind(styles);

export const LINK_CONTEXT_MENU = 'LinkContextMenu';
interface LinkContextMenuProps {
    composeLinkViewState: ComposeLinkViewState;
    anchorElement: HTMLAnchorElement;
    editor: Editor;
    composeId: string;
    recipientInfo: SharingTipRecipientInfo[];
}

let hasSharingTip = false;

@observer
class LinkContextMenu extends React.Component<LinkContextMenuProps, {}> {
    render() {
        // When the context menu is shown & user uses Ctrl+Z to undo beautification,
        // the sharingLink associated with the linkId would be removed as the old linkId is considered as being removed.
        const sharingLinkInfo = getSharingLink(this.props.composeLinkViewState.linkId);
        if (!sharingLinkInfo) {
            return null;
        }

        const items = getContextMenuItems(
            this.props.composeLinkViewState,
            this.props.anchorElement,
            this.props.editor,
            this.props.composeId,
            this.props.recipientInfo
        );

        // If the sharing dialog flight is on, the permission menu item will always have an icon
        const conditionalStyles = {
            conditionalPadding: hasSharingTip || isFeatureEnabled('doc-attachment-sharingDialog'),
        };

        return (
            <ContextualMenu
                className={classNames(conditionalStyles)}
                target={this.props.anchorElement}
                shouldFocusOnMount={false}
                onDismiss={this.onDismissContextMenu}
                items={items}
                directionalHint={DirectionalHint.bottomLeftEdge}
                gapSpace={2}
            />
        );
    }

    private onDismissContextMenu = (ev: any) => {
        closeLinkContextMenu();
    };
}

let contextMenuDiv: HTMLDivElement = null;
export default function showLinkContextMenu(
    anchorElement: HTMLAnchorElement,
    editor: Editor,
    composeId: string,
    recipientWells: RecipientWellWithFindControlViewState[],
    fromAddress: string
) {
    if (!isFeatureEnabled('doc-beautify-link')) {
        return;
    }

    closeLinkContextMenu();

    const recipientInfo: SharingTipRecipientInfo[] = getSharingTipRecipientInfo(
        recipientWells,
        fromAddress
    );

    contextMenuDiv = document.createElement('div');
    contextMenuDiv.id = 'sharingLinkContextMenu';
    anchorElement.appendChild(contextMenuDiv);

    ReactDOM.render(
        <LinkContextMenu
            composeLinkViewState={getComposeLinkViewState(anchorElement.id)}
            anchorElement={anchorElement}
            editor={editor}
            composeId={composeId}
            recipientInfo={recipientInfo}
        />,
        contextMenuDiv
    );

    setIsContextMenuOpen(anchorElement.id, true);
}

function getContextMenuItems(
    composeLinkViewState: ComposeLinkViewState,
    anchorElement: HTMLAnchorElement,
    editor: Editor,
    composeId: string,
    recipientInfo: SharingTipRecipientInfo[]
): IContextualMenuItem[] {
    const contextMenuItems: IContextualMenuItem[] = [];
    const contextMenuOptions = getLinkContextMenuOptions();
    const replaceLinkHandler: ReplaceLinkHandler = (url: string, displayText: string) => {
        if (!editor.isDisposed()) {
            editor.addUndoSnapshot(() => updateLink(editor, anchorElement, url, displayText));
        }
    };

    contextMenuOptions.forEach(contextMenu => {
        switch (contextMenu) {
            case LinkContextMenuOptions.SharingTip:
                const sharingTipContextMenuItem = getSharingTipContextMenuItem(
                    composeLinkViewState,
                    recipientInfo
                );
                if (sharingTipContextMenuItem) {
                    hasSharingTip = true;
                    contextMenuItems.push(sharingTipContextMenuItem);
                    contextMenuItems.push(getDividerForContextMenu('divider_sharing'));
                } else {
                    hasSharingTip = false;
                }
                break;
            case LinkContextMenuOptions.OpenInNewTab:
                const OpenInNewTabMenuItem = getOpenInNewTabContextMenuItem(
                    getSharingLink(composeLinkViewState.linkId).url,
                    closeLinkContextMenu
                );
                if (OpenInNewTabMenuItem) {
                    contextMenuItems.push(OpenInNewTabMenuItem);
                }
                break;
            case LinkContextMenuOptions.ChangePermission:
                const ChangePermissionMenuItem = getChangePermissionContextMenuItem(
                    composeLinkViewState
                );
                if (ChangePermissionMenuItem) {
                    contextMenuItems.push(ChangePermissionMenuItem);
                    contextMenuItems.push(getDividerForContextMenu('divider_perms'));
                }
                break;
            case LinkContextMenuOptions.ChangePermissionSharingDialog:
                const ChangePermissionSharingDialogMenuItem = getChangePermissionSharingDialogContextMenuItem(
                    composeLinkViewState.linkId
                );
                if (ChangePermissionSharingDialogMenuItem) {
                    contextMenuItems.push(ChangePermissionSharingDialogMenuItem);
                    contextMenuItems.push(getDividerForContextMenu('divider_perms'));
                }
                break;
            case LinkContextMenuOptions.EditLink:
                if (composeLinkViewState.isLinkBeautified) {
                    const EditLinkContextMenuItem = getEditLinkContextMenuItem(
                        anchorElement,
                        replaceLinkHandler
                    );
                    if (EditLinkContextMenuItem) {
                        contextMenuItems.push(EditLinkContextMenuItem);
                    }
                }
                break;
            case LinkContextMenuOptions.RevertToFullUrl:
                if (composeLinkViewState.isLinkBeautified) {
                    contextMenuItems.push(
                        getRevertToFullUrlContextMenuItem(
                            composeLinkViewState.linkId,
                            anchorElement,
                            replaceLinkHandler,
                            composeId
                        )
                    );
                }
                break;
            case LinkContextMenuOptions.Preview:
                if (isFeatureEnabled('doc-sharepoint-link-preview')) {
                    contextMenuItems.push(
                        getLinkPreviewContextMenuItem(
                            getSharingLink(composeLinkViewState.linkId).url
                        )
                    );
                }
                break;
            default:
                assertNever(contextMenu);
        }
    });

    return contextMenuItems;
}

export function closeLinkContextMenu() {
    if (!isFeatureEnabled('doc-beautify-link')) {
        return;
    }

    if (contextMenuDiv) {
        ReactDOM.unmountComponentAtNode(contextMenuDiv);
        if (contextMenuDiv.parentNode) {
            contextMenuDiv.parentNode.removeChild(contextMenuDiv);
        }
        contextMenuDiv = null;
    }

    resetIsContextMenuOpenForLinks();
}

function updateLink(
    editor: Editor,
    anchorElement: HTMLAnchorElement,
    url: string,
    displayText: string
) {
    // Remove the anchor element & insert a new anchor element
    ReactDOM.unmountComponentAtNode(anchorElement);
    const newAnchor: HTMLAnchorElement = document.createElement('a');
    newAnchor.href = url;
    newAnchor.innerText = displayText;
    editor.replaceNode(anchorElement, newAnchor);
    editor.select(newAnchor, PositionType.After);
    editor.triggerContentChangedEvent(LINK_CONTEXT_MENU);
}

export type ReplaceLinkHandler = (url: string, displayText: string) => void;
